import { ReactNode, useEffect, useRef } from "react";
import Grid from "@/config/Grid/Grid";
import styles from "./OrderActionBar.module.scss";

interface OrderActionBarProps {
  /** Optional label/value row shown above the buttons. */
  summaryLabel?: string;
  summaryValue?: string;
  children: ReactNode;
}

/**
 * The fixed bottom bar for the ordering flow.
 *
 * The global TransparentDrawer renders nothing on /order/* (no case matches, so
 * it hits its `default`), which leaves the bottom of the screen to this. Like
 * that component, it measures itself into a CSS custom property so pages can
 * pad their scroll area and never hide content underneath.
 */
function OrderActionBar(props: OrderActionBarProps) {
  const { summaryLabel, summaryValue, children } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const node = wrapperRef.current;
    if (!node) return;

    const publishHeight = () => {
      root.style.setProperty(
        "--order-bottom-bar-height",
        `${node.offsetHeight}px`,
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
      root.style.setProperty("--order-bottom-bar-height", "0px");
    };
  }, []);

  return (
    <div ref={wrapperRef} className={styles.orderActionBarWrapper}>
      <Grid>
        <div className={`start-1 col-4 ${styles.orderActionBarContent}`}>
          {summaryLabel !== undefined ? (
            <div className={styles.orderActionBarSummary}>
              <p className="body-bold-medium">{summaryLabel}</p>
              <p className={`body-medium ${styles.orderActionBarSummaryValue}`}>
                {summaryValue}
              </p>
            </div>
          ) : null}
          {children}
        </div>
      </Grid>
    </div>
  );
}

export default OrderActionBar;
